import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { GoogleLogin } from '@react-oauth/google';
import { FlaskConical, Cloud, Microscope, ClipboardList, BarChart3, FileText, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ThemedText, Button, Input } from '../components';

// Helper function to decode JWT token
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

// --- Keyframe Animations ---

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const bubbleFloat = keyframes`
  0% {
    transform: translateY(100%) scale(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100vh) scale(1);
    opacity: 0;
  }
`;

const shimmer = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
`;

const dashAnimation = keyframes`
  to {
    stroke-dashoffset: -40;
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 0.6; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const glowPulse = keyframes`
  0% { filter: drop-shadow(0 0 4px rgba(89, 202, 238, 0.3)); }
  50% { filter: drop-shadow(0 0 12px rgba(89, 202, 238, 0.7)); }
  100% { filter: drop-shadow(0 0 4px rgba(89, 202, 238, 0.3)); }
`;

const waveMove = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

// --- Layout ---

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background-color: ${props => props.theme.colors.background};

  @media (max-width: 968px) {
    flex-direction: column;
  }
`;

const LoginLeftPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xxl};
  position: relative;
  background-color: ${props => props.theme.colors.background};
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 968px) {
    padding: ${props => props.theme.spacing.lg};
  }
`;

const LoginRightPanel = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #0f1229 0%, #232562 30%, #2d3070 55%, #3a6b8a 75%, #59caee 100%);
  background-size: 300% 300%;
  animation: ${gradientShift} 12s ease infinite;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xxl};
  color: white;
  position: relative;
  overflow: hidden;

  @media (max-width: 968px) {
    min-height: 400px;
    padding: ${props => props.theme.spacing.xl};
  }
`;

// --- Left Panel Components ---

const LoginCard = styled.div`
  width: 100%;
  max-width: 460px;
  position: relative;
  background: ${props => props.theme.colors.backgroundSecondary};
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid ${props => props.theme.colors.border};
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 968px) {
    padding: 28px;
  }
`;

const LogoContainer = styled.div`
  text-align: left;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const LogoImage = styled.img`
  width: 130px;
  height: auto;
  margin-bottom: ${props => props.theme.spacing.md};
`;

// --- Right Panel Components ---

const RightPanelContent = styled.div`
  text-align: center;
  max-width: 520px;
  position: relative;
  z-index: 10;
  animation: ${fadeInUp} 1s ease-out 0.3s both;
`;

const RightPanelTitle = styled.h1`
  font-size: 38px;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.md};
  line-height: 1.25;
  letter-spacing: -0.5px;
  background: linear-gradient(to right, #ffffff, #b3e5fc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 968px) {
    font-size: 28px;
  }
`;

const RightPanelText = styled.p`
  font-size: 16px;
  opacity: 0.85;
  line-height: 1.7;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: rgba(255, 255, 255, 0.9);

  @media (max-width: 968px) {
    font-size: 14px;
  }
`;

// --- Floating Bubbles ---

const Bubble = styled.div`
  position: absolute;
  bottom: -20px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(89, 202, 238, 0.25), rgba(89, 202, 238, 0.05));
  border: 1px solid rgba(89, 202, 238, 0.15);
  animation: ${bubbleFloat} ${props => props.$duration}s linear infinite;
  animation-delay: ${props => props.$delay}s;
  left: ${props => props.$left}%;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  pointer-events: none;
`;

const FloatingBubbles = () => {
  const bubbles = [
    { $size: 12, $left: 10, $duration: 14, $delay: 0 },
    { $size: 8, $left: 25, $duration: 18, $delay: 3 },
    { $size: 16, $left: 40, $duration: 12, $delay: 1 },
    { $size: 6, $left: 55, $duration: 20, $delay: 5 },
    { $size: 20, $left: 70, $duration: 15, $delay: 2 },
    { $size: 10, $left: 85, $duration: 17, $delay: 4 },
    { $size: 14, $left: 15, $duration: 16, $delay: 7 },
    { $size: 7, $left: 60, $duration: 19, $delay: 6 },
    { $size: 18, $left: 90, $duration: 13, $delay: 8 },
    { $size: 9, $left: 35, $duration: 21, $delay: 9 },
  ];

  return (
    <>
      {bubbles.map((b, i) => (
        <Bubble key={i} {...b} />
      ))}
    </>
  );
};

// --- Wave Effect ---

const WaveContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 80px;
  overflow: hidden;
  z-index: 5;
`;

const WaveSvg = styled.svg`
  position: absolute;
  bottom: 0;
  width: 200%;
  height: 100%;
  animation: ${waveMove} 8s linear infinite;
`;

const WaveEffect = () => (
  <WaveContainer>
    <WaveSvg viewBox="0 0 1440 80" preserveAspectRatio="none">
      <path
        d="M0,40 C120,65 240,10 360,40 C480,70 600,15 720,40 C840,65 960,10 1080,40 C1200,70 1320,15 1440,40 C1560,65 1680,10 1800,40 C1920,70 2040,15 2160,40 C2280,65 2400,10 2520,40 C2640,70 2760,15 2880,40 L2880,80 L0,80 Z"
        fill="rgba(89, 202, 238, 0.08)"
      />
    </WaveSvg>
    <WaveSvg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ animationDuration: '12s', animationDirection: 'reverse' }}>
      <path
        d="M0,50 C180,20 360,70 540,45 C720,20 900,65 1080,45 C1260,25 1440,60 1620,45 C1800,30 1980,65 2160,45 C2340,25 2520,60 2700,45 C2880,30 2880,80 2880,80 L0,80 Z"
        fill="rgba(89, 202, 238, 0.05)"
      />
    </WaveSvg>
  </WaveContainer>
);

// --- Ambient Glow ---

const AmbientGlow = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(89, 202, 238, 0.15) 0%, transparent 70%);
  top: ${props => props.$top || '20%'};
  left: ${props => props.$left || '50%'};
  transform: translate(-50%, -50%);
  pointer-events: none;
  animation: ${shimmer} ${props => props.$duration || '4s'} ease-in-out infinite;
  animation-delay: ${props => props.$delay || '0s'};
`;

// --- Digital Thread Animation ---

const AnimationWrapper = styled.div`
  width: 100%;
  height: 180px;
  margin-bottom: 1.5rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${glowPulse} 4s ease-in-out infinite;
`;

const SvgContainer = styled.svg`
  width: 100%;
  height: 100%;
  overflow: visible;

  .path {
    fill: none;
    stroke: rgba(255, 255, 255, 0.12);
    stroke-width: 2;
  }

  .active-path {
    fill: none;
    stroke: rgba(89, 202, 238, 0.7);
    stroke-width: 2;
    stroke-dasharray: 8 12;
    animation: ${dashAnimation} 1s linear infinite;
  }

  .icon-bg {
    fill: rgba(255, 255, 255, 0.06);
    stroke: rgba(89, 202, 238, 0.4);
    stroke-width: 1.5;
  }

  .icon {
    font-size: 24px;
    fill: white;
    text-anchor: middle;
    dominant-baseline: middle;
  }

  .label {
    fill: rgba(255, 255, 255, 0.7);
    font-size: 10px;
    text-anchor: middle;
    letter-spacing: 1.5px;
    font-weight: 600;
  }
`;

const IconGroup = styled.g`
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${props => props.$delay || '0s'};
`;

const PulseCircle = styled.circle`
  fill: rgba(89, 202, 238, 0.15);
  animation: ${pulse} 3s ease-in-out infinite;
  animation-delay: ${props => props.$delay || '0s'};
  transform-origin: center;
`;

const DigitalThreadAnimation = () => {
  return (
    <AnimationWrapper>
      <SvgContainer viewBox="0 0 400 150">
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Connection Paths */}
        <path d="M 80 70 Q 140 20, 200 70" className="path" />
        <path d="M 80 70 Q 140 20, 200 70" className="active-path" filter="url(#glow)" />

        <path d="M 200 70 Q 260 120, 320 70" className="path" />
        <path d="M 200 70 Q 260 120, 320 70" className="active-path" style={{ animationDirection: 'reverse' }} filter="url(#glow)" />

        {/* Node 1: Sample */}
        <IconGroup $delay="0s">
          <circle cx="80" cy="70" r="32" className="icon-bg" />
          <PulseCircle cx="80" cy="70" r="26" $delay="0s" />
          <foreignObject x="67" y="59" width="26" height="26">
            <FlaskConical size={26} color="white" />
          </foreignObject>
          <text x="80" y="115" className="label">SAMPLE</text>
        </IconGroup>

        {/* Node 2: Cloud */}
        <IconGroup $delay="-2s">
          <circle cx="200" cy="70" r="42" className="icon-bg" />
          <PulseCircle cx="200" cy="70" r="35" $delay="-1s" />
          <foreignObject x="184" y="54" width="32" height="32">
            <Cloud size={32} color="white" />
          </foreignObject>
          <text x="200" y="125" className="label">CLOUD</text>
        </IconGroup>

        {/* Node 3: Lab */}
        <IconGroup $delay="-4s">
          <circle cx="320" cy="70" r="32" className="icon-bg" />
          <PulseCircle cx="320" cy="70" r="26" $delay="-2s" />
          <foreignObject x="307" y="59" width="26" height="26">
            <Microscope size={26} color="white" />
          </foreignObject>
          <text x="320" y="115" className="label">LAB</text>
        </IconGroup>
      </SvgContainer>
    </AnimationWrapper>
  );
};

// --- Feature Badges ---

const FeatureBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: ${props => props.theme.spacing.md};
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.3px;
  backdrop-filter: blur(4px);
  animation: ${fadeInUp} 0.8s ease-out ${props => props.$delay || '0s'} both;
`;

const BadgeIcon = styled.span`
  display: inline-flex;
  align-items: center;
`;

// --- Form Components ---

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme.colors.danger}10;
  border: 1px solid ${props => props.theme.colors.danger}30;
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.danger};
  text-align: center;
  font-size: ${props => props.theme.typography.sizes?.small};
`;

const DemoBanner = styled.div`
  background-color: ${props => props.theme.colors.tint}10;
  border: 1px solid ${props => props.theme.colors.tint}30;
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.tint};
  text-align: center;
  font-size: ${props => props.theme.typography.sizes?.small};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ForgotPasswordLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  text-decoration: none;
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes?.small};
  padding: 0;
  margin-top: ${props => props.theme.spacing.sm};
  align-self: center;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: ${props => props.theme.spacing.lg} 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }

  &::before {
    margin-right: ${props => props.theme.spacing.md};
  }

  &::after {
    margin-left: ${props => props.theme.spacing.md};
  }
`;

const DividerText = styled.span`
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes?.small};
  font-weight: 500;
`;

const GoogleButtonContainer = styled.div`
  display: flex;
  justify-content: center;

  & > div {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

// --- Page Component ---

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [demoBanner, setDemoBanner] = useState(null);

  const { login, googleLogin, isAuthenticated } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  // Pre-fill credentials for a public read-only demo when ?demo={slug} is present.
  // Anonymous call (no token) — fail silently and fall back to the normal login form.
  useEffect(() => {
    const slug = new URLSearchParams(window.location.search).get('demo');
    if (!slug) return;
    const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';
    fetch(`${base}/api/public/demos/${encodeURIComponent(slug)}`)
      .then(res => (res.ok ? res.json() : null))
      .then(demo => {
        if (!demo) return;
        if (demo.demoLoginEmail) setEmail(demo.demoLoginEmail);
        if (demo.demoLoginPassword) setPassword(demo.demoLoginPassword);
        setDemoBanner(demo.name || 'read-only');
      })
      .catch(() => { /* silently fall back to normal login */ });
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const validateForm = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setLoginError('');

    try {
      const result = await login(email, password);

      if (!result.success) {
        setLoginError(result.error || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setLoginError('');

    try {
      const decoded = decodeJWT(credentialResponse.credential);

      if (!decoded) {
        setLoginError('Failed to process Google login. Please try again.');
        return;
      }

      const googleData = {
        email: decoded.email,
        firstName: decoded.given_name || '',
        lastName: decoded.family_name || '',
        profilePicture: decoded.picture || '',
        googleId: decoded.sub
      };

      const result = await googleLogin(googleData);

      if (!result.success) {
        setLoginError(result.error || 'Google login failed. Please try again.');
      }
    } catch (error) {
      console.error('Google login error:', error);
      setLoginError('An unexpected error occurred during Google login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
    setLoginError('Google login failed. Please try again.');
  };

  return (
    <LoginContainer theme={theme}>
      <LoginLeftPanel theme={theme}>
        <LoginCard theme={theme}>
          <LogoContainer>
            <LogoImage src="/img/aqrate-logo.png" alt="aQRate Logo" theme={theme} />
            <ThemedText type="title" style={{ fontSize: '28px', marginBottom: theme.spacing.sm, fontWeight: 700 }}>
              Welcome Back
            </ThemedText>
            <ThemedText type="default" style={{ color: theme.colors.muted, fontSize: '15px' }}>
              Sign in to your aQRate portal account
            </ThemedText>
          </LogoContainer>

          {demoBanner && (
            <DemoBanner theme={theme}>
              You're entering the {demoBanner} read-only demo.
            </DemoBanner>
          )}

          <Form onSubmit={handleSubmit}>
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              error={errors.email}
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              error={errors.password}
            />

            {loginError && (
              <ErrorMessage theme={theme}>
                {loginError}
              </ErrorMessage>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={loading}
              style={{ marginTop: theme.spacing.sm }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>

            <ForgotPasswordLink theme={theme} type="button" onClick={() => navigate('/forgot-password')}>
              Forgot your password?
            </ForgotPasswordLink>
          </Form>

          <Divider theme={theme}>
            <DividerText theme={theme}>OR</DividerText>
          </Divider>

          <GoogleButtonContainer theme={theme}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
              width="100%"
            />
          </GoogleButtonContainer>

          <div style={{ textAlign: 'center', marginTop: theme.spacing.md }}>
            <span style={{ fontSize: '11px', color: theme.colors.muted }}>
              v{import.meta.env.VITE_VERSION || '1.0.0'}
              {import.meta.env.VITE_ENVIRONMENT && import.meta.env.VITE_ENVIRONMENT !== 'production' && (
                <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '4px', background: theme.colors.warning + '20', color: theme.colors.warning, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase' }}>
                  {import.meta.env.VITE_ENVIRONMENT}
                </span>
              )}
            </span>
          </div>
        </LoginCard>
      </LoginLeftPanel>

      <LoginRightPanel theme={theme}>
        <FloatingBubbles />
        <AmbientGlow $top="15%" $left="30%" $duration="5s" $delay="0s" />
        <AmbientGlow $top="70%" $left="75%" $duration="7s" $delay="2s" />

        <RightPanelContent>
          <DigitalThreadAnimation />

          <RightPanelTitle>
            Harmonize Your Data Flow<br />
            from Source to System
          </RightPanelTitle>
          <RightPanelText>
            Streamline field data collection, sample tracking, and reporting
            with our digital thread technology.
          </RightPanelText>

          <FeatureBadges>
            <Badge $delay="1.0s"><BadgeIcon><ClipboardList size={14} /></BadgeIcon> Custom Forms</Badge>
            <Badge $delay="1.15s"><BadgeIcon><BarChart3 size={14} /></BadgeIcon> Real-time Analytics</Badge>
            <Badge $delay="1.3s"><BadgeIcon><FileText size={14} /></BadgeIcon> Reports</Badge>
            <Badge $delay="1.45s"><BadgeIcon><Lock size={14} /></BadgeIcon> Secure & Compliant</Badge>
          </FeatureBadges>
        </RightPanelContent>

        <WaveEffect />
      </LoginRightPanel>
    </LoginContainer>
  );
};
